import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProductValidationModal = ({ 
  isOpen, 
  onClose, 
  product,
  onValidate 
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [selectedPayload, setSelectedPayload] = useState('order.created');

  const samplePayloads = {
    'order.created': {
      event: 'order.created',
      timestamp: new Date()?.toISOString(),
      data: {
        orderId: 'ORD-2025-001',
        customerId: 'CUST-12345',
        status: 'pending',
        total: 299.99,
        currency: 'USD',
        items: [
          {
            productId: 'PROD-001',
            name: 'Premium Headphones',
            quantity: 1,
            price: 299.99
          }
        ],
        shippingAddress: {
          street: '123 Main St',
          city: 'New York',
          state: 'NY',
          zipCode: '10001',
          country: 'US'
        }
      }
    },
    'order.updated': {
      event: 'order.updated',
      timestamp: new Date()?.toISOString(),
      data: {
        orderId: 'ORD-2025-001',
        previousStatus: 'pending',
        newStatus: 'processing',
        updatedAt: new Date()?.toISOString()
      }
    },
    'payment.completed': {
      event: 'payment.completed',
      timestamp: new Date()?.toISOString(),
      data: {
        paymentId: 'PAY-2025-001',
        orderId: 'ORD-2025-001',
        amount: 299.99,
        currency: 'USD',
        method: 'credit_card',
        status: 'completed',
        transactionId: 'TXN-ABC123'
      }
    },
    'inventory.updated': {
      event: 'inventory.updated',
      timestamp: new Date()?.toISOString(),
      data: {
        productId: 'PROD-001',
        sku: 'HP-PREM-001',
        previousStock: 50,
        newStock: 49,
        location: 'WAREHOUSE-NYC'
      }
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    setValidationResult(null);

    try {
      // Simulate product validation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock validation result
      const success = Math.random() > 0.3; // 70% success rate for demo
      const result = {
        success,
        statusCode: success ? 200 : 500,
        responseTime: Math.floor(Math.random() * 500) + 100,
        response: success 
          ? { message: 'Product received successfully', processed: true }
          : { error: 'Internal server error', message: 'Failed to process product' },
        timestamp: new Date()?.toISOString(),
        payload: samplePayloads?.[selectedPayload]
      };
      
      setValidationResult(result);
      
      if (onValidate) {
        onValidate(product, result);
      }
    } catch (error) {
      setValidationResult({
        success: false,
        error: error?.message,
        timestamp: new Date()?.toISOString()
      });
    } finally {
      setIsValidating(false);
    }
  };

  if (!isOpen || !product) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-90" onClick={onClose} />
      {/* Modal */}
      <div className="fixed inset-0 z-100 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Test Product: {product?.name}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Send a test payload to validate your product endpoint
                </p>
              </div>
              <Button
                variant="ghost"
                size="xs"
                iconName="X"
                onClick={onClose}
              />
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Product Info */}
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Product" size={16} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground">{product?.name}</h3>
                    <p className="text-xs text-muted-foreground">Endpoint URL</p>
                  </div>
                </div>
                <code className="text-xs bg-card px-3 py-2 rounded border font-mono block">
                  {product?.url}
                </code>
              </div>

              {/* Payload Selection */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-foreground">Select Test Payload</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.keys(samplePayloads)?.map((eventType) => (
                    <button
                      key={eventType}
                      onClick={() => setSelectedPayload(eventType)}
                      className={`
                        p-3 rounded-lg border text-left transition-smooth
                        ${selectedPayload === eventType
                          ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 text-foreground'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        <Icon 
                          name={selectedPayload === eventType ? 'CheckCircle' : 'Circle'} 
                          size={16} 
                        />
                        <span className="text-sm font-medium">
                          {eventType?.replace('.', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                        </span>
                      </div>
                      <p className="text-xs opacity-70">
                        Test {eventType} event payload
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Payload Preview */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-foreground">Payload Preview</h3>
                <div className="bg-muted/30 rounded-lg p-4">
                  <pre className="text-xs font-mono text-foreground overflow-x-auto">
                    {JSON.stringify(samplePayloads?.[selectedPayload], null, 2)}
                  </pre>
                </div>
              </div>

              {/* Validation Result */}
              {validationResult && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-foreground">Validation Result</h3>
                  <div className={`
                    rounded-lg p-4 border
                    ${validationResult?.success 
                      ? 'bg-success/5 border-success/20' :'bg-error/5 border-error/20'
                    }
                  `}>
                    <div className="flex items-center space-x-3 mb-3">
                      <Icon 
                        name={validationResult?.success ? 'CheckCircle' : 'XCircle'} 
                        size={20} 
                        className={validationResult?.success ? 'text-success' : 'text-error'}
                      />
                      <div>
                        <p className={`text-sm font-medium ${
                          validationResult?.success ? 'text-success' : 'text-error'
                        }`}>
                          {validationResult?.success ? 'Product Test Successful' : 'Product Test Failed'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(validationResult.timestamp)?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-card/50 rounded p-3">
                        <p className="text-xs text-muted-foreground">Status Code</p>
                        <p className={`text-sm font-medium ${
                          validationResult?.statusCode === 200 ? 'text-success' : 'text-error'
                        }`}>
                          {validationResult?.statusCode}
                        </p>
                      </div>
                      <div className="bg-card/50 rounded p-3">
                        <p className="text-xs text-muted-foreground">Response Time</p>
                        <p className="text-sm font-medium text-foreground">
                          {validationResult?.responseTime}ms
                        </p>
                      </div>
                      <div className="bg-card/50 rounded p-3">
                        <p className="text-xs text-muted-foreground">Event Type</p>
                        <p className="text-sm font-medium text-foreground">
                          {selectedPayload}
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Response Body:</p>
                      <pre className="text-xs font-mono bg-card/50 p-3 rounded overflow-x-auto">
                        {JSON.stringify(validationResult?.response, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
              <div className="text-xs text-muted-foreground">
                This will send a POST request to your product endpoint
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isValidating}
                >
                  Close
                </Button>
                <Button
                  onClick={handleValidate}
                  loading={isValidating}
                  iconName="TestTube"
                  iconPosition="left"
                >
                  {isValidating ? 'Testing...' : 'Send Test Request'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductValidationModal;